import { useState, useRef, useEffect } from "react";
import { Sparkles, X, Send, Loader2, ExternalLink, ThumbsUp, ThumbsDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ViduraWidget = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async (query) => {
    setBusy(true);
    setMsgs((m) => [...m, { q: query, a: null }]);
    try {
      const r = await fetch(`${API}/vidura/ask`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query, history: msgs.filter((m) => m.a).map((m) => ({ q: m.q, a: m.a })) }),
      });
      const d = r.ok ? await r.json() : { answer: "Vidura is busy — please try again shortly.", web: [] };
      setMsgs((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, a: d.answer, web: d.web, whatsapp: d.whatsapp, qa_id: d.qa_id } : x)));
    } catch {
      setMsgs((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, a: "Network hiccup — try again." } : x)));
    }
    setBusy(false);
  };

  const ask = async (e) => {
    e.preventDefault();
    const query = q.trim();
    if (query.length < 3 || busy) return;
    setQ("");
    send(query);
  };

  useEffect(() => {
    const h = (e) => { const p = e.detail?.prompt; if (!p || busy) return; setOpen(true); send(p); };
    window.addEventListener("vidura:ask", h);
    return () => window.removeEventListener("vidura:ask", h);
  }, [msgs, busy]); // eslint-disable-line

  const rate = async (i, helpful) => {
    const m = msgs[i];
    if (!m?.qa_id || m.rated) return;
    setMsgs((ms) => ms.map((x, j) => (j === i ? { ...x, rated: helpful ? "up" : "down", teaching: !helpful } : x)));
    fetch(`${API}/vidura/feedback`, { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ qa_id: m.qa_id, helpful }) }).catch(() => {});
  };
  const teach = async (i, correction) => {
    const m = msgs[i];
    if (!m?.qa_id || !correction.trim()) return;
    setMsgs((ms) => ms.map((x, j) => (j === i ? { ...x, teaching: false, taught: true } : x)));
    fetch(`${API}/vidura/feedback`, { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ qa_id: m.qa_id, helpful: false, correction }) }).catch(() => {});
  };

  return (
    <div data-no-translate>
      <button onClick={() => setOpen(!open)} data-testid="vidura-fab" aria-label="Ask Vidura, the Growthifye Vidura agent"
        className="fixed bottom-6 left-6 z-40 group flex items-center gap-2.5 bg-gf-surface/95 backdrop-blur border border-gf-primary/50 pl-1.5 pr-4 py-1.5 rounded-full shadow-lg hover:border-gf-primary transition-colors">
        <img src="/vidura-avatar.png" alt="Vidura — Growthifye Vidura sage" className="w-9 h-9 rounded-full object-cover" />
        <span className="font-body font-bold text-xs text-gf-text flex items-center gap-1.5">
          {open ? <X className="w-3.5 h-3.5 text-gf-primary" /> : <Sparkles className="w-3.5 h-3.5 text-gf-primary" />}
          Ask Vidura
        </span>
      </button>
      {open && (
        <div className="fixed bottom-24 left-6 z-40 w-[92vw] max-w-[400px] border border-gf-primary/30 bg-gf-surface flex flex-col shadow-2xl" style={{ maxHeight: "72vh" }} data-testid="vidura-panel">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
            <img src="/vidura-avatar.png" alt="Vidura avatar" className="w-10 h-10 rounded-full object-cover border border-gf-primary/40" />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-gf-primary flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Vidura — Growthifye Vidura agent</p>
              <p className="font-body text-[10px] text-gf-muted mt-0.5">Ask about clean energy, BESS, green finance, tenders or our services.</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4" data-testid="vidura-messages">
            {!msgs.length && <p className="font-body text-xs text-gf-muted">Namaste! I'm Vidura. Try: "What does Growthifye do?", "Is BESS viable for my factory?", "Latest solar tariff trends in India"</p>}
            {msgs.map((m, i) => (
              <div key={i}>
                <p className="font-body text-xs text-gf-text bg-white/5 border border-white/10 px-3 py-2 inline-block">{m.q}</p>
                {m.a === null ? (
                  <p className="mt-2 font-body text-xs text-gf-muted flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin text-gf-primary" /> Vidura is thinking…</p>
                ) : (
                  <div className="mt-2">
                    <p className="font-body text-xs text-gf-muted leading-relaxed whitespace-pre-wrap" data-testid={`vidura-answer-${i}`}>{m.a}</p>
                    {m.qa_id && (
                      <span className="mt-1.5 inline-flex items-center gap-2">
                        <button onClick={() => rate(i, true)} data-testid={`vidura-up-${i}`} title="Helpful — Vidura learns from this"
                          className={`${m.rated === "up" ? "text-gf-primary" : "text-gf-muted hover:text-gf-primary"}`} disabled={!!m.rated}>
                          <ThumbsUp className="w-3 h-3" /></button>
                        <button onClick={() => rate(i, false)} data-testid={`vidura-down-${i}`} title="Not helpful"
                          className={`${m.rated === "down" ? "text-red-400" : "text-gf-muted hover:text-red-400"}`} disabled={!!m.rated}>
                          <ThumbsDown className="w-3 h-3" /></button>
                        {m.rated === "up" && <span className="font-mono text-[8px] uppercase text-gf-primary">learned ✓</span>}
                        {m.taught && <span className="font-mono text-[8px] uppercase text-gf-primary">lesson saved ✓</span>}
                      </span>
                    )}
                    {m.teaching && (
                      <form className="flex gap-1.5 mt-2" onSubmit={(e) => { e.preventDefault(); teach(i, e.target.elements[`fix${i}`].value); }}>
                        <input name={`fix${i}`} autoFocus data-testid={`vidura-teach-${i}`} placeholder="Teach Vidura the right answer…"
                          className="flex-1 bg-gf-bg border border-white/15 px-2 py-1.5 font-body text-[11px] text-gf-text focus:border-gf-primary focus:outline-none" />
                        <button type="submit" data-testid={`vidura-teach-submit-${i}`}
                          className="border border-gf-primary/40 text-gf-primary font-body text-[10px] px-2.5 hover:bg-gf-primary/10">Teach</button>
                      </form>
                    )}
                    {m.whatsapp && (
                      <a href={m.whatsapp} target="_blank" rel="noreferrer" data-testid={`vidura-whatsapp-${i}`}
                        className="mt-2 inline-flex items-center gap-1.5 bg-[#25D366] text-black font-body font-bold text-[11px] px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity">
                        <Send className="w-3 h-3" /> Connect on Growthifye's WhatsApp
                      </a>
                    )}
                    {!!m.web?.length && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {m.web.map((w, j) => (
                          <a key={j} href={w.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 font-mono text-[8px] uppercase border border-white/15 text-gf-muted px-1.5 py-0.5 hover:text-gf-primary">
                            <ExternalLink className="w-2.5 h-2.5" /> {w.title.slice(0, 28)}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <form onSubmit={ask} className="p-3 border-t border-white/10 flex gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask Vidura anything…" data-testid="vidura-input"
              className="flex-1 bg-transparent border border-white/15 px-3 py-2 font-body text-xs text-gf-text focus:border-gf-primary outline-none" />
            <button type="submit" disabled={busy || q.trim().length < 3} data-testid="vidura-send"
              className="border border-gf-primary/40 text-gf-primary px-3 hover:bg-gf-primary/10 disabled:opacity-50"><Send className="w-3.5 h-3.5" /></button>
          </form>
        </div>
      )}
    </div>
  );
};
